import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserListParams, UserTypes } from "../../../../@types/User";
import { SelectInput } from "../../../../components/SelectInput";
import { wrapperRequests } from "../../../../services/api";
import { routesURL } from "../../../../services/routesUrl";
import { ToastPosition, toast } from "react-toastify";
import { UsersPermissionsContext } from "../../../../contexts/UsersPermissions";
import { useContextSelector } from "use-context-selector";
import * as S from "./styles";
import { PageHeader } from "../../../../components/Headers/PageHeader";
import { Table } from "../../../../components/New/Table";
import { ColumnDef } from "@tanstack/react-table";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { SearchInput } from "../../../../components/New/SearchInput";
import { Modal } from "../../../../components/New/Modal";
import { DeleteModal } from "../../../../components/New/Modal/components/DeleteModal";
import { DeleteIcon } from "../../../../components/New/Modal/components/DeleteIcon";
import { Button } from "../../../../components/New/Button";
import { Loading } from "../../../../components/New/Loading";
import { NoDataAvailableImage } from "../../../../components/NoDataAvailableImage";
import { useMediaQuery } from "usehooks-ts";
import { useTheme } from "styled-components";
import { DrawerComponent } from "../../../../components/New/Drawer";
import { DefaultRowsPerPage } from "../../../../components/New/Table/constants";
import { useTranslation } from "react-i18next";
import { RoleName } from "../../../../@types/Permissions";

interface Toastify {
  success: string;
  error: string;
}

export function Users() {
  const { t, i18n } = useTranslation();

  const { getAllRoles, allRoles } = useContextSelector(
    UsersPermissionsContext,
    (context) => context,
  );

  const updatedParams = new URLSearchParams();
  const navigate = useNavigate();
  const [userSearchParams, setUserSearchParams] = useSearchParams();
  const [users, setUsers] = useState<UserTypes[]>([]);
  const [userListParams, setUserListParams] = useState<UserListParams>({
    name: userSearchParams.get("name") ?? "",
    page: userSearchParams.get("page") ?? "1",
    role: userSearchParams.get("role") ?? "",
    rowsPerPage: userSearchParams.get("limit") || DefaultRowsPerPage,
    sortBy: userSearchParams.get("sortBy") ?? "",
    totalPages: userSearchParams.get("totalPages") ?? "0",
  });
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const matches = useMediaQuery(`(max-width: ${theme.responsiveDesign.sm})`);

  const isEnglishLanguage = i18n.language.startsWith("en");

  const [deleteDrawerOpen, setDeleteDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserTypes | null>(null);

  const getUsers = async () => {
    setLoading(true);
    try {
      const page = userSearchParams.get("page") ?? "1";
      const rowsPerPage = userSearchParams.get("limit") || DefaultRowsPerPage;
      const role = userSearchParams.get("role") ?? "";
      const name = userSearchParams.get("name") ?? "";
      const sortBy = userSearchParams.get("sortBy") ?? "";
      const { data } = await wrapperRequests(
        routesURL.settings.users.getAllUsers,
        "GET",
        {
          params: { page, limit: rowsPerPage, role, name, sortBy },
        },
      );
      setUsers(data.content);
      setUserListParams((state) => ({
        ...state,
        totalPages: data.totalPages || "0",
      }));
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setLoading(false);
    }
  };

  const toastify = (
    type: keyof Toastify,
    message: string,
    position: ToastPosition,
  ) => {
    toast[type](message, {
      position,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  async function handleDeleteUser(id: string) {
    setLoading(true);
    try {
      await wrapperRequests(`/users/${id}`, "DELETE");
      setUsers((state) => state.filter((user) => user.id !== id));
      toastify("success", "🚴‍♂️ " + t("userDeletedSuccessfully"), "top-center");
    } catch (error) {
      toastify("error", t("userDeleteError"), "top-center");
    } finally {
      setLoading(false);
    }
  }

  const updateSearchParams = () => {
    userListParams?.name && updatedParams.set("name", userListParams.name);
    userListParams?.role && updatedParams.set("role", userListParams.role);
    updatedParams.set("limit", String(userListParams.rowsPerPage));
    updatedParams.set("page", "1");
    setUserSearchParams(updatedParams);
  };

  const updateUserParams = (key: keyof UserListParams, value: string) => {
    setUserListParams((state) => ({
      ...state,
      [key]: value,
    }));
  };

  const updateRows = (value: number) => {
    setUserSearchParams((params) => {
      const updatedParams = new URLSearchParams(params.toString());
      updatedParams.set("limit", String(value));
      updatedParams.set("page", "1");
      return updatedParams;
    });
  };

  const updatePage = (value: number) => {
    setUserSearchParams((params) => {
      const updatedParams = new URLSearchParams(params.toString());
      updatedParams.set("page", String(value));
      return updatedParams;
    });
  };

  function rolesNames(rolesQtd: number) {
    if (rolesQtd === 0) return t("custom");
    if (rolesQtd === 1) return;
    if (rolesQtd > 1) return t("rolesMoreThanOne");
  }

  const usersColumns: ColumnDef<UserTypes>[] = [
    {
      accessorKey: "name",
      header: t("name"),
      size: 265,
      cell: (props) => <p>{props.getValue()}</p>,
    },
    {
      accessorKey: "email",
      header: t("email"),
      size: 265,
      cell: (props) => <p>{props.getValue()}</p>,
    },
    {
      accessorFn: (row) => row?.roles[0]?.name || t("custom"),
      header: t("roles"),
      size: 265,
      cell: (props) => (
        <S.RolesContainer
          role={
            rolesNames(props.row.original.roles.length)
              ? rolesNames(props.row.original.roles.length)
              : String(props.row.original.roles.map((role) => role.name))
          }
        >
          <p>{props.getValue()}</p>
        </S.RolesContainer>
      ),
    },
    {
      accessorKey: "",
      size: 100,
      header: t("actions"),
      cell: (props) => (
        <S.IconContainer>
          <MdOutlineModeEditOutline
            size={24}
            style={{ cursor: "pointer" }}
            title={t("edit")}
            onClick={() =>
              navigate(`/settings/users/${props.row.original.id}`, {
                state: { id: props.row.original.id },
              })
            }
          />
          {matches ? (
            <DeleteIcon
              title={t("delete")}
              onClick={() => {
                setSelectedUser(props.row.original);
                setDeleteDrawerOpen(true);
              }}
            />
          ) : (
            <Modal
              customButtons
              modalContent={DeleteModal({
                message: t("confirmDeleteUser", {
                  name: props.row.original.name,
                }),
                onClick: () => handleDeleteUser(props.row.original.id),
                isLoading: loading,
              })}
              title={t("userDelete")}
              width="21.25rem"
            >
              <DeleteIcon title={t("delete")} />
            </Modal>
          )}
        </S.IconContainer>
      ),
    },
  ];

  useEffect(() => {
    const next = {
      name: userSearchParams.get("name") ?? "",
      page: userSearchParams.get("page") ?? "1",
      role: userSearchParams.get("role") ?? "",
      rowsPerPage: userSearchParams.get("limit") || DefaultRowsPerPage,
      sortBy: userSearchParams.get("sortBy") ?? "",
      totalPages: userSearchParams.get("totalPages") ?? "0",
    } as UserListParams;

    setUserListParams((prev) => ({ ...prev, ...next }));
    getUsers();
    getAllRoles();
  }, [userSearchParams]);

  return (
    <S.Container>
      <PageHeader
        title={t("users")}
        description={t("seeAllUsersDescription")}
        customButton
      >
        <Button
          title={t("newUser")}
          width={isEnglishLanguage ? "7.5rem" : "auto"}
          onClick={() => navigate("/settings/users/create")}
        />
      </PageHeader>

      <S.Content>
        <SearchInput
          disabled={loading}
          onPressEnter={() => updateSearchParams()}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            updateUserParams("name", event.target.value);
          }}
          value={userListParams.name}
          filterDropdown={{
            disabled: loading,
            selectInput: (
              <SelectInput
                placeholder={t("selectAnOption")}
                options={allRoles.map((role) => ({
                  id: role.id,
                  value: role.name,
                  label: role.name,
                }))}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                  updateUserParams("role", event.target.value);
                }}
                value={userListParams.role}
                variant="resized"
              />
            ),
            onFilterClick: () => updateSearchParams(),
            onResetClick: () => updateUserParams("role", ""),
          }}
        />

        {loading && <Loading />}

        {users.length === 0 && !loading && <NoDataAvailableImage />}

        {users.length > 0 && !loading && (
          <Table
            columns={usersColumns}
            columnToFilter="name"
            data={users}
            key={users.length}
            pagination={{
              rowsPerPage: Number(userListParams.rowsPerPage),
              selectPage: updatePage,
              selectRowsPerPage: updateRows,
              page: Number(userListParams.page),
              totalPages: Number(userListParams.totalPages),
            }}
          />
        )}
      </S.Content>

      {matches && deleteDrawerOpen && selectedUser && (
        <DrawerComponent
          open={deleteDrawerOpen}
          onOpenChange={setDeleteDrawerOpen}
          triggerContent={null}
          description=""
          title={t("deleteUser")}
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <p>{t("confirmDeleteUser", { name: selectedUser.name })}</p>

            <Button
              title={t("confirm")}
              variant="destructive"
              width="100%"
              onClick={() => {
                handleDeleteUser(selectedUser.id);
                setDeleteDrawerOpen(false);
              }}
            />
            <Button
              title={t("cancel")}
              variant="ghost"
              width="100%"
              onClick={() => setDeleteDrawerOpen(false)}
            />
          </div>
        </DrawerComponent>
      )}
    </S.Container>
  );
}
